import { useCallback, useEffect, useRef, useState } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useIdleWarehouses, useUserDetail, useUserPermissions, usePlant, useCheckpoints, useOrgs } from "../../services/hooks";
import { Button, Checkbox, DatePicker, Dialog, Dropdown, Loader, tCell, toastMessage } from "../utils";
import { buttonClasses } from "../utils/theme";
import { GenericTableInfinite, TablePaginationActions } from "../utils/GenericTable";
import dayjs from "dayjs";
import { useGetReportFieldsMutation, useGetReportMutation } from "../../api/api";
import { useGetVehiclesQuery } from "../../api/vmsSlice";
import { debounce } from "../../services/functions";
import { Grid, TablePagination } from "@mui/material";
import { Navigate } from "react-router-dom";
import { downloadExcel } from "react-export-table-to-excel";
import DateRangePicker from "../utils/GenericDateRangeFilter/GenericDateRangeFilter";
import "./Report.css";
import { usePlantContext } from "../../context/plantContext";

const defaultFilter = {
	startdate: "",
	enddate: "",
	pagesize: 10,
	page_no: 1,
};

const REPORT_TYPE_OPTIONS = [
	{ label: "Vehicle Movement", value: "vehicleStats", permissionKey: "viewReport" },
	{ label: "Fastag Report", value: "fastagReport", permissionKey: "fastagReport" },
	{ label: "Vaahan Report For RFID", value: "vaahanReportForRFID", permissionKey: "vaahanReportForRFID" },
	{ label: "Vaahan Report For ANPR", value: "vaahanReportForANPR", permissionKey: "vaahanReportForANPR" },
	{ label: "Sarathi Report", value: "sarathiReport", permissionKey: "sarathiReport" },
];
const FIELDS_TO_IGNORE_ALL_SELECT = ["Checkpoint Name"];
const DRAG_TYPE = "FIELD_ITEM";

const EXTRA_FILTERS_REGISTRY = {
	vehicleStats: [
		{
			renderer: VehicleSearchFilter,
			showFilterBy: true,
			filterKeys: ["vehicleId", "vehicleIdName"],
			getPayload: (filter) => ({
				searchBy: "vehicleNumber",
				search: filter?.vehicleIdName || "",
			}),
		},
	],
	fastagReport: [
		{
			renderer: VehicleSearchFilter,
			filterKeys: ["vehicleId", "vehicleIdName"],
			getPayload: (filter) => ({
				vehicleNumber: filter?.vehicleIdName || "",
			}),
		},
		{
			renderer: ApiResponseFilter,
			filterKeys: ["apiResponse"],
			getPayload: (filter) => ({
				searchBy: "statusCategory",
				search: filter?.apiResponse || "",
			}),
		},
	],
	vaahanReportForRFID: [
		{
			renderer: VehicleSearchFilter,
			filterKeys: ["vehicleId", "vehicleIdName"],
			getPayload: (filter) => ({
				vehicleNumber: filter?.vehicleIdName || "",
			}),
		},
		{
			renderer: ApiResponseFilter,
			filterKeys: ["apiResponse"],
			getPayload: (filter) => ({
				searchBy: "statusCategory",
				search: filter?.apiResponse || "",
			}),
		},
	],
	vaahanReportForANPR: [
		{
			renderer: VehicleSearchFilter,
			filterKeys: ["vehicleId", "vehicleIdName"],
			getPayload: (filter) => ({
				vehicleNumber: filter?.vehicleIdName || "",
			}),
		},
		{
			renderer: ApiResponseFilter,
			filterKeys: ["apiResponse"],
			getPayload: (filter) => ({
				searchBy: "statusCategory",
				search: filter?.apiResponse || "",
			}),
		},
	],
	sarathiReport: [
		{
			renderer: VehicleSearchFilter,
			filterKeys: ["vehicleId", "vehicleIdName"],
			getPayload: (filter) => ({
				vehicleNumber: filter?.vehicleIdName || "",
			}),
		},
		{
			renderer: ApiResponseFilter,
			filterKeys: ["apiResponse"],
			getPayload: (filter) => ({
				searchBy: "statusCategory",
				search: filter?.apiResponse || "",
			}),
		},
	],
};

function getExtraPayloadFields(reportTypeValue, filter) {
	return (EXTRA_FILTERS_REGISTRY[reportTypeValue] || []).reduce((acc, entry) => ({ ...acc, ...entry.getPayload(filter) }), {});
}

function getExtraFilterKeys(reportTypeValue) {
	return (EXTRA_FILTERS_REGISTRY[reportTypeValue] || []).flatMap((entry) => entry.filterKeys || []);
}

const Report = () => {
	const { userDetail, isSuperAdmin } = useUserDetail();
	const { userPermissions, isFetched } = useUserPermissions();
	const { plants, getPlantDetails } = usePlant();
	const { getReport, clearData, reportData, isReportLoading, totalCount } = useGetReport();
	const { fetchReportPdf, isPDFLoading } = useGetReportPdf();

	const [idleWarehouseOpen, setIdleWarehouseOpen] = useState(false);
	const [filter, setFilter] = useState(defaultFilter);
	const filteredDefaultReportType = isSuperAdmin ? REPORT_TYPE_OPTIONS[0] : REPORT_TYPE_OPTIONS.find((option) => userPermissions?.[option.permissionKey]?.value);
	const [selectedReportType, setSelectedReportType] = useState(filteredDefaultReportType || null);
	const [selectedFields, setSelectedFields] = useState([]);
	const [customizedFieldEnabled, setCustomizedFieldEnabled] = useState(false);
	const [arrangeDrawerOpen, setArrangeDrawerOpen] = useState(false);
	let { checkpointOptions, getCheckpoints } = useCheckpoints(null, "reportCheckpoints");
	checkpointOptions = checkpointOptions.map((option) => ({ ...option, value: option.checkpointNumber }));

	useEffect(() => {
		if (!isSuperAdmin && plants?.length == 1) {
			getCheckpoints({ forReport: true, plantIds: plants[0]._id });
		}
		clearData();
	}, []);

	useEffect(() => {
		if (selectedReportType?.value !== "vehicleStats") return;
		if (!filter.plantIds?.length) return;
		getCheckpoints({ forReport: true, plantIds: filter.plantIds });
	}, [selectedReportType?.value, filter.plantIds]);

	const { fieldMapping = [], isLoading: fieldMappingLoading } = useReportFieldMapping({ type: selectedReportType?.value });

	useEffect(() => {
		if (fieldMapping?.length && customizedFieldEnabled) {
			setSelectedFields((prev) => {
				if (prev.length > 0) return prev;
				return fieldMapping
					.filter((field) => !FIELDS_TO_IGNORE_ALL_SELECT.includes(field.field_name))
					.map((field) => ({ label: field.field_name, value: field.field_id }));
			});
		}
	}, [fieldMapping, customizedFieldEnabled]);

	const { plantOpts, stateOpts, roOpts } = plants.reduce(
		(res, plant) => {
			let showPlant = true;
			if (filter.regionalOffice?.length) showPlant = filter.regionalOffice.includes(plant.regionalOffice);
			if (filter.states?.length) showPlant = filter.states.includes(plant.state);
			if (showPlant) res.plantOpts.push({ label: plant.plantName, value: plant._id, data: plant });

			if (plant.regionalOffice && !res.roOpts.find((_) => _.value == plant.regionalOffice)) {
				if (filter.plantIds?.length) {
					if (filter.plantIds.includes(plant._id)) res.roOpts.push({ label: plant.regionalOffice, value: plant.regionalOffice });
				} else if (filter.states?.length) {
					if (filter.states.includes(plant.state)) res.roOpts.push({ label: plant.regionalOffice, value: plant.regionalOffice });
				} else {
					res.roOpts.push({ label: plant.regionalOffice, value: plant.regionalOffice });
				}
			}

			if (plant.state && !res.stateOpts.find((_) => _.value == plant.state)) {
				if (filter.plantIds?.length) {
					if (filter.plantIds.includes(plant._id)) res.stateOpts.push({ label: plant.state, value: plant.state });
				} else if (filter.regionalOffice?.length) {
					if (filter.regionalOffice.includes(plant.regionalOffice)) res.stateOpts.push({ label: plant.state, value: plant.state });
				} else {
					res.stateOpts.push({ label: plant.state, value: plant.state });
				}
			}
			return res;
		},
		{ plantOpts: [], roOpts: [], stateOpts: [] }
	);

	useEffect(() => {
		if (plants.length > 0 && plantOpts.length === 1) {
			setFilter((prev) => {
				let updated = { ...prev };
				if (roOpts.length === 1) updated.regionalOffice = [roOpts[0].value];
				if (stateOpts.length === 1) updated.states = [stateOpts[0].value];
				if (plantOpts.length === 1) updated.plantIds = [plantOpts[0].value];
				return updated;
			});
		}
	}, [plants]);

	const handleFilterChange = (e, _name) => {
		const name = _name ? _name : e.target.name;
		const value = e?.$d
			? new Date(e.$d).toISOString()
			: _name
				? Array.isArray(e)
					? e.map((option) => option.value)
					: e?.value || ""
				: e.target.value;

		const shouldResetPage = name !== "page_no" && name !== "pagesize";
		const pageReset = shouldResetPage ? { page_no: 1 } : {};

		if (name === "vehicleId" || name === "assetsId") {
			setFilter((old) => ({ ...old, ...pageReset, [name]: value, [`${name}Name`]: e?.label || "" }));
		} else if (name === "plantId") {
			if (selectedReportType?.value === "vehicleStats") {
				setFilter((old) => ({ ...old, ...pageReset, plantIds: value, checkpointsFrom: "", checkpointsTo: "", viaCheckpoints: [] }));
				getCheckpoints({ forReport: true, plantIds: value });
			} else {
				setFilter((old) => ({ ...old, ...pageReset, plantIds: value }));
			}
		} else if (name === "regionalOffice") {
			setFilter((old) => ({
				...old,
				...pageReset,
				regionalOffice: value,
				plantIds: value.length && old.plantIds?.length ? old.plantIds.filter((pid) => plants.find((p) => p._id == pid && value.includes(p.regionalOffice))) : [],
			}));
		} else if (name === "state") {
			setFilter((old) => ({
				...old,
				...pageReset,
				states: value,
				plantIds: value.length && old.plantIds?.length ? old.plantIds.filter((pid) => plants.find((p) => p._id == pid && value.includes(p.state))) : [],
			}));
		} else if (name === "checkpointsFrom" || name === "checkpointsTo") {
			setFilter((old) => ({ ...old, ...pageReset, [name]: value, viaCheckpoints: [] }));
		} else if (name === "orgId") {
			setFilter((old) => ({ ...old, ...pageReset, orgId: value }));
			getPlantDetails({ orgId: value });
		} else {
			setFilter((old) => ({ ...old, ...pageReset, [name]: value }));
		}
	};

	const handleReportTypeChange = (option) => {
		setSelectedReportType(option);
		setSelectedFields([]);
		setCustomizedFieldEnabled(false);
		const keysToReset = getExtraFilterKeys(selectedReportType?.value);
		if (keysToReset.length) {
			setFilter((old) => {
				const updated = { ...old };
				keysToReset.forEach((key) => {
					updated[key] = undefined;
				});
				return updated;
			});
		}
	};

	const handleCustomizedFieldToggle = (e) => {
		const checked = e.target.checked;
		setCustomizedFieldEnabled(checked);
		if (checked) {
			if (!selectedFields.length && fieldMapping?.length) {
				setSelectedFields(
					fieldMapping
						.filter((field) => !FIELDS_TO_IGNORE_ALL_SELECT.includes(field.field_name))
						.map((field) => ({ label: field.field_name, value: field.field_id }))
				);
			}
			setArrangeDrawerOpen(true);
		} else {
			setSelectedFields([]);
			setArrangeDrawerOpen(false);
		}
	};

	const handleFieldsReorder = (reorderedFields) => setSelectedFields(reorderedFields);

	const handlePageChange = (newPage) => {
		setFilter((old) => ({ ...old, page_no: newPage }));
	};

	const handlePageSizeChange = (newSize) => {
		setFilter((old) => ({ ...old, pagesize: newSize, page_no: 1 }));
	};

	function handleFormValidation(arg = {}) {
		const { type = "html" } = arg;
		let errorMsg = "";
		if (selectedReportType?.value === "vehicleStats") {
			if (!filter.checkpointsFrom && filter.checkpointsTo) errorMsg = "Please select checkpoints";
			if (filter.checkpointsFrom && !filter.checkpointsTo) errorMsg = "Please select checkpoints";

			const fromCheckpoint = checkpointOptions?.find((checkpoint) => filter.checkpointsFrom == checkpoint.checkpointNumber);
			const toCheckpoint = checkpointOptions?.find((checkpoint) => filter.checkpointsTo == checkpoint.checkpointNumber);
			const difference = (toCheckpoint?.checkpointNumber ?? 0) - (fromCheckpoint?.checkpointNumber ?? 0);
			if (difference < 0) errorMsg = "Selected checkpoints are incorrect";
		}
		if (!filter.startdate || !filter.enddate) errorMsg = "Please select date range";

		if (errorMsg) {
			toastMessage(false, errorMsg);
			return;
		}

		const payload = makePayload(type);
		if (type === "pdf") handlePdfDownload(payload);
		else getReport(payload);
	}

	function makePayload(download_type = "html") {
		let _plantIds = filter.plantIds?.length ? filter.plantIds : undefined;
		if (!_plantIds) {
			let _plants;
			if (filter.regionalOffice?.length) _plants = plants.filter((_) => filter.regionalOffice.includes(_.regionalOffice));
			if (filter.states?.length) _plants = (_plants ?? plants).filter((_) => filter.states.includes(_.state));
			if (_plants?.length) _plantIds = _plants.map((_) => _._id);
		}

		return {
			type: selectedReportType?.value,
			...(filter.startdate ? { startdate: filter.startdate } : {}),
			...(filter.enddate ? { enddate: filter.enddate } : {}),
			...(selectedReportType?.value === "vehicleStats"
				? {
					checkpointFrom: filter.checkpointsFrom,
					checkpointTo: filter.checkpointsTo,
					viaCheckpoints: filter.viaCheckpoints,
				}
				: {}),
			selected_fields: customizedFieldEnabled
				? selectedFields.map((field) => field.value)
				: fieldMapping?.length
					? fieldMapping.filter((field) => !FIELDS_TO_IGNORE_ALL_SELECT.includes(field.field_name)).map((field) => field.field_id)
					: [],
			orgId: filter?.orgId,
			download_type,
			deviceId: sessionStorage.getItem("deviceId") || "",
			pagesize: filter.pagesize,
			page_no: filter.page_no,
			...(_plantIds ? { plantIds: _plantIds } : {}),
			...getExtraPayloadFields(selectedReportType?.value, filter),
		};
	}

	const handlePdfDownload = async (payload) => {
		const {
			data: { error, message, data },
		} = await fetchReportPdf(payload);
		if (error) {
			toastMessage(false, message);
			return;
		}

		const byteCharacters = atob(data.toString("base64"));
		const byteArray = new Uint8Array([...byteCharacters].map((char) => char.charCodeAt(0)));
		const url = URL.createObjectURL(new Blob([byteArray], { type: "application/pdf" }));
		Object.assign(document.createElement("a"), { href: url, download: `vehicle_report_${new Date()}.pdf` }).click();
		URL.revokeObjectURL(url);
	};

	if (isFetched && userPermissions && Object.keys(userPermissions).length && !userPermissions.viewReport?.value && userDetail?.userType != "superAdmin") {
		return <Navigate to="/not-authorized" replace />;
	}

	return (
		<DndProvider backend={HTML5Backend}>
			<div className="contentpanel">
				<div className="cust-row flex-algn-cent" style={{ justifyContent: "space-between" }}>
					<div className="cust-col-5">
						<h1 className="title">Report</h1>
					</div>
					{isSuperAdmin || userPermissions?.viewIdlePlants?.value ? (
						<div style={{ paddingBottom: "5px" }}>
							<Button disabled={false} text="Idle Warehouses" onClick={() => setIdleWarehouseOpen(true)} style={buttonClasses.lynkitOrangeFill} />
						</div>
					) : null}
				</div>
				<hr className="bgg" />

				<FilterSection
					selectedReportType={selectedReportType}
					onReportTypeChange={handleReportTypeChange}
					onFilterChange={handleFilterChange}
					checkpointOptions={checkpointOptions}
					filter={filter}
					filteredPlantOptions={plantOpts}
					onSubmit={handleFormValidation}
					onPageChange={handlePageChange}
					onPageSizeChange={handlePageSizeChange}
					customizedFieldEnabled={customizedFieldEnabled}
					onCustomizedFieldToggle={handleCustomizedFieldToggle}
					onOpenDrawer={() => setArrangeDrawerOpen(true)}
					reportData={reportData}
					isReportLoading={isReportLoading}
					isPDFLoading={isPDFLoading}
					totalCount={totalCount}
					fieldMappingEnabled={customizedFieldEnabled}
				/>

				<ConfigureColumnsDrawer
					open={arrangeDrawerOpen}
					onClose={() => setArrangeDrawerOpen(false)}
					fieldMapping={fieldMapping}
					fieldMappingLoading={fieldMappingLoading}
					selectedFields={selectedFields}
					onReorder={handleFieldsReorder}
				/>

				<IdleWarehouseDialog open={idleWarehouseOpen} onClose={() => setIdleWarehouseOpen(false)} />
			</div>
		</DndProvider>
	);
};

function FilterSection({
	selectedReportType,
	onReportTypeChange,
	onFilterChange,
	checkpointOptions,
	filteredPlantOptions,
	filter,
	onSubmit,
	onPageChange,
	onPageSizeChange,
	customizedFieldEnabled,
	onCustomizedFieldToggle,
	onOpenDrawer,
	reportData,
	isReportLoading,
	isPDFLoading,
	totalCount,
}) {
	const { isSuperAdmin } = useUserDetail();
	const { plantOptions } = usePlantContext();
	const { userPermissions } = useUserPermissions();
	const filteredReportTypeOptions = REPORT_TYPE_OPTIONS.filter((option) => {
		if (isSuperAdmin) return true;
		return userPermissions?.[option.permissionKey]?.value;
	});
	const { orgOptions } = useOrgs();
	const orgValue = orgOptions.find((option) => option.value == filter.orgId) || null;
	const plantValue = filteredPlantOptions.filter((option) => filter.plantIds?.includes(option.value));
	const checkpointsFromValue = filter?.checkpointsFrom ? checkpointOptions?.find((checkpoint) => filter.checkpointsFrom == checkpoint.value) || null : null;
	const checkpointsToValue = filter?.checkpointsTo ? checkpointOptions?.find((checkpoint) => filter.checkpointsTo == checkpoint.value) || null : null;
	const viaTo = filter?.viaCheckpoints ? checkpointOptions?.filter((checkpoint) => filter.viaCheckpoints.includes(checkpoint.value)) : [];

	const filterRef = useRef({ page_no: "1", pagesize: "25", search: "", searchBy: "vehicle" });
	const { data: vehicleData, refetch } = useGetVehiclesQuery(filterRef.current, {
		refetchOnMountOrArgChange: true,
	});
	const { data: vehicles = [] } = vehicleData || {};

	useEffect(() => {
		if (!reportData) return;
		onSubmit();
	}, [filter.page_no, filter.pagesize]);

	let disableOption = [];
	let disableViaOption = [];
	if (checkpointsFromValue?.checkpointNumber) {
		disableOption = checkpointOptions?.filter((checkpoint) => checkpoint.checkpointNumber < checkpointsFromValue.checkpointNumber);
	}
	if (checkpointsFromValue?.checkpointNumber && checkpointsToValue?.checkpointNumber) {
		disableViaOption = checkpointOptions?.filter(
			(checkpoint) => checkpoint.checkpointNumber <= checkpointsFromValue.checkpointNumber || checkpoint.checkpointNumber >= checkpointsToValue.checkpointNumber
		);
	}

	return (
		<>
			<div className="fitler-selection-cont" style={{ marginTop: "15px" }}>
				<Dropdown id="reportType" name="reportType" label="Report Type" options={filteredReportTypeOptions} value={selectedReportType} onChange={onReportTypeChange} />
				{isSuperAdmin ? <Dropdown id="orgId" name="orgId" label="Organization" onChange={onFilterChange} options={orgOptions} value={orgValue} /> : null}
				{(Array.isArray(plantOptions) && plantOptions.length > 1 || isSuperAdmin)? (
					<Dropdown id="plantFilter" name="plantId" label="Warehouses" onChange={onFilterChange} options={filteredPlantOptions} value={plantValue} multiple disableCloseOnSelect />
				) : null}
				{selectedReportType?.value === "vehicleStats" && plantValue?.length == 1 ? (
					<>
						<Dropdown id="checkpointsFrom" name="checkpointsFrom" label="Checkpoints From" options={checkpointOptions} onChange={onFilterChange} value={checkpointsFromValue} />
						<Dropdown
							id="viaTo"
							name="viaCheckpoints"
							label="Via To"
							options={checkpointOptions}
							onChange={onFilterChange}
							disabled={checkpointsFromValue && checkpointsToValue ? false : true}
							getOptionDisabled={(option) => {
								return disableViaOption?.length > 0 ? disableViaOption.some((filterItem) => option.checkpointNumber === filterItem.checkpointNumber) : false;
							}}
							value={viaTo || null}
							multiple
						/>
						<Dropdown
							id="checkpointsTo"
							name="checkpointsTo"
							label="Checkpoints To"
							options={checkpointOptions}
							onChange={onFilterChange}
							getOptionDisabled={(option) => {
								return disableOption?.length > 0 ? disableOption.some((filterItem) => filterItem.checkpointNumber === option.checkpointNumber) : false;
							}}
							value={checkpointsToValue}
						/>
					</>
				) : null}
				<ExtraFilters reportType={selectedReportType?.value} filter={filter} onFilterChange={onFilterChange} />
				<div>
					<DateRangePicker
						onDateRangeSelect={(data) => {
							onFilterChange({ value: data?.startDate || "" }, "startdate");
							onFilterChange({ value: data?.endDate || "" }, "enddate");
						}}
					/>
				</div>
			</div>

			<div style={{ marginTop: "1rem", display: "flex", alignItems: "center", gap: "1rem" }}>
				<Checkbox
					id="customizedField"
					name="customizedField"
					label="Configure Report Columns"
					checked={customizedFieldEnabled}
					onChange={onCustomizedFieldToggle}
					value="customizedField"
				/>
				{customizedFieldEnabled ? (
					<div>
						<Button onClick={onOpenDrawer} text="Edit Columns" style={buttonClasses.lynkitOrangeEmpty} />
					</div>
				) : null}
			</div>

			<Grid container spacing="0.5rem" justifyContent="center" mt="1rem">
				<Grid item xl={2} lg={2} md={2} sm={3} xs={6}>
					<Button text="Get Report" onClick={() => !isReportLoading && onSubmit()} disabled={isReportLoading || isPDFLoading} style={buttonClasses.lynkitOrangeFill} />
				</Grid>
				<Grid item xl={2} lg={2} md={2} sm={3} xs={6}>
					<Button text="Export To Excel" onClick={() => !isReportLoading && onSubmit({ type: "excel" })} disabled={isReportLoading || isPDFLoading} style={buttonClasses.lynkitGreenFill} />
				</Grid>
			</Grid>

			{isReportLoading || isPDFLoading ? (
				<Loader />
			) : reportData?.includes("<html>") ? (
				<div>
					<div dangerouslySetInnerHTML={{ __html: reportData?.replace(/\n/g, "") }} className="html-cont" />
					<TablePagination
						component="div"
						count={totalCount}
						page={filter.page_no - 1}
						onPageChange={(eventOrPage, maybePage) => {
							const newPage = typeof maybePage === "number" ? maybePage : eventOrPage;
							onPageChange(newPage + 1);
						}}
						rowsPerPage={filter.pagesize}
						onRowsPerPageChange={(e) => onPageSizeChange(Number(e.target.value))}
						ActionsComponent={TablePaginationActions}
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							color: "var(--text-color)",
							backgroundColor: "var(--table-pagination-bg)",
							"& .MuiTablePagination-selectLabel": { margin: "0" },
							"& .MuiTablePagination-displayedRows": { margin: "0" },
							"& .MuiTablePagination-selectIcon": { color: "var(--text-color)" },
						}}
					/>
				</div>
			) : null}
		</>
	);
}

function ExtraFilters({ reportType, filter, onFilterChange }) {
	const entries = EXTRA_FILTERS_REGISTRY[reportType] || [];
	if (!entries.length) return null;

	return (
		<>
			{entries.map((entry, i) => {
				const Renderer = entry.renderer;
				if (!Renderer) return null;
				return <Renderer key={`${reportType}-${i}`} config={entry} filter={filter} onFilterChange={onFilterChange} />;
			})}
		</>
	);
}

const ApiResponseOption = [
	{ label: "Success", value: "success" },
	{ label: "Not Found", value: "not_found" },
];

function VehicleSearchFilter({ config, filter, onFilterChange }) {
	const filterRef = useRef({ page_no: "1", pagesize: "25", search: "", searchBy: "vehicle" });

	const { data: vehicleData, refetch } = useGetVehiclesQuery(filterRef.current, {
		refetchOnMountOrArgChange: true,
	});
	const { data: vehicles = [] } = vehicleData || {};

	const vehicleValue = filter?.vehicleId ? vehicles.filter((v) => v._id === filter.vehicleId).map((v) => ({ label: v.truck_no, value: v._id }))[0] || null : null;

	const handleVehicleChange = (e, _name) => {
		const value = _name ? (Array.isArray(e) ? e.map((_) => _.value) : e?.value || "") : e?.target?.value;
		if (!value) {
			filterRef.current = { page_no: "1", pagesize: "25", search: "", searchBy: "vehicle" };
			refetch();
		}
		onFilterChange(e, "vehicleId");
	};

	const handleInputChange = debounce((e) => {
		filterRef.current = { page_no: "1", pagesize: "25", search: e.target.value, searchBy: "vehicle" };
		refetch();
	}, 500);

	return (
		<>
			{config?.showFilterBy ? (
				<Dropdown
					id="filterBy"
					name="filterBy"
					label="Filter By"
					options={[{ label: "Vehicle Number", value: "vehicleNumber" }]}
					value={{ label: "Vehicle Number", value: "vehicleNumber" }}
				/>
			) : null}
			<Dropdown
				id="vehicleId"
				name="vehicleId"
				label="Search Vehicle Number"
				onChange={handleVehicleChange}
				onInputChange={handleInputChange}
				options={vehicles.map((v) => ({ label: v.truck_no, value: v._id }))}
				value={vehicleValue}
			/>
		</>
	);
}

function ApiResponseFilter({ filter, onFilterChange }) {
	const apiResponseValue = ApiResponseOption.find((option) => option.value === filter?.apiResponse) || null;

	return (
		<>
			<Dropdown
				id="apiResponseFilterBy"
				name="apiResponseFilterBy"
				label="Filter By"
				options={[{ label: "Status Category", value: "statusCategory" }]}
				value={{ label: "Status Category", value: "statusCategory" }}
			/>
			<Dropdown id="apiResponse" name="apiResponse" label="API Response" options={ApiResponseOption} value={apiResponseValue} onChange={onFilterChange} />
		</>
	);
}

function ConfigureColumnsDrawer({ open, onClose, fieldMapping, fieldMappingLoading, selectedFields, onReorder }) {
	const [localFields, setLocalFields] = useState([]);
	const [checkedValues, setCheckedValues] = useState(new Set());

	useEffect(() => {
		if (!open) return;
		if (!fieldMapping?.length) {
			setLocalFields([]);
			return;
		}

		const selected = selectedFields.length
			? [...selectedFields]
			: fieldMapping
				.filter((field) => !FIELDS_TO_IGNORE_ALL_SELECT.includes(field.field_name))
				.map((field) => ({ label: field.field_name, value: field.field_id }));

		const selectedValues = new Set(selected.map((field) => field.value));
		const unselected = fieldMapping
			.filter((field) => !selectedValues.has(field.field_id))
			.map((field) => ({ label: field.field_name, value: field.field_id }));

		setLocalFields([...selected, ...unselected]);
	}, [open, fieldMapping, selectedFields]);

	useEffect(() => {
		if (!open) return;
		setCheckedValues(
			new Set(
				(selectedFields.length
					? selectedFields
					: fieldMapping?.filter((field) => !FIELDS_TO_IGNORE_ALL_SELECT.includes(field.field_name)).map((field) => ({ value: field.field_id })) || []
				).map((field) => field.value)
			)
		);
	}, [open, fieldMapping, selectedFields]);

	const moveField = useCallback((dragIndex, hoverIndex) => {
		setLocalFields((prev) => {
			const next = [...prev];
			const [removed] = next.splice(dragIndex, 1);
			next.splice(hoverIndex, 0, removed);
			return next;
		});
	}, []);

	const handleCheckChange = (field, checked) => {
		setCheckedValues((prev) => {
			const next = new Set(prev);
			if (checked) {
				if (field.label === "Checkpoint Name") {
					const checkpointDetail = localFields.find((item) => item.label === "Checkpoint Detail");
					if (checkpointDetail) next.delete(checkpointDetail.value);
				} else if (field.label === "Checkpoint Detail") {
					const checkpointName = localFields.find((item) => item.label === "Checkpoint Name");
					if (checkpointName) next.delete(checkpointName.value);
				}
				next.add(field.value);
			} else {
				next.delete(field.value);
			}
			return next;
		});
	};

	const selectableFieldIds = (fieldMapping || [])
		.filter((field) => !FIELDS_TO_IGNORE_ALL_SELECT.includes(field.field_name))
		.map((field) => field.field_id);

	const isAllSelected = selectableFieldIds.length ? selectableFieldIds.every((fieldId) => checkedValues.has(fieldId)) : false;

	const handleSelectAll = (e) => {
		setCheckedValues(e.target.checked ? new Set(selectableFieldIds) : new Set());
	};

	const handleApply = () => {
		onReorder(localFields.filter((field) => checkedValues.has(field.value)));
		onClose();
	};

	if (!open) return null;

	return (
		<>
			<div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1200 }} />
			<div className="drawer-panel">
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1rem 1.25rem", borderBottom: "1px solid var(--primary-color, #2e343c)" }}>
					<span style={{ color: "var(--text-color)", fontWeight: 600, fontSize: "1rem" }}>Configure Report Columns</span>
					<button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-color)", fontSize: "1.2rem", padding: "0.2rem 0.4rem" }}>
						✕
					</button>
				</div>

				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.6rem 1.25rem 0.4rem", borderBottom: "1px solid var(--primary-color, #2e343c)" }}>
					<Checkbox id="selectAll" name="selectAll" checked={isAllSelected} onChange={handleSelectAll} label="Select All" value="All" />
					<p style={{ margin: 0, fontSize: "0.75rem", color: "var(--text-color)", opacity: 0.5 }}>Drag ⠿ to reorder</p>
				</div>

				<div style={{ flex: 1, overflowY: "auto", padding: "0.25rem 1rem" }}>
					{fieldMappingLoading ? (
						<Loader height="40px" size="1.5rem" />
					) : localFields.length === 0 ? (
						<p style={{ color: "var(--text-color)", opacity: 0.4, fontSize: "0.85rem", textAlign: "center", marginTop: "2rem" }}>No fields available.</p>
					) : (
						localFields.map((field, index) => (
							<DraggableCheckboxItem
								key={field.value}
								index={index}
								field={field}
								checked={checkedValues.has(field.value)}
								moveField={moveField}
								onCheckChange={handleCheckChange}
							/>
						))
					)}
				</div>

				<div style={{ padding: "1rem 1.25rem", borderTop: "1px solid var(--primary-color, #2e343c)", display: "flex", gap: "0.75rem" }}>
					<Button text="Apply" onClick={handleApply} style={buttonClasses.lynkitOrangeFill} />
					<Button text="Cancel" onClick={onClose} style={buttonClasses.lynkitOrangeEmpty} />
				</div>
			</div>
		</>
	);
}

function DraggableCheckboxItem({ field, index, moveField, checked, onCheckChange }) {
	const ref = useRef(null);

	const [{ handlerId }, drop] = useDrop({
		accept: DRAG_TYPE,
		collect: (monitor) => ({ handlerId: monitor.getHandlerId() }),
		hover(item, monitor) {
			if (!ref.current) return;
			const dragIndex = item.index;
			const hoverIndex = index;
			if (dragIndex === hoverIndex) return;

			const { top, bottom } = ref.current.getBoundingClientRect();
			const hoverMiddleY = (bottom - top) / 2;
			const hoverClientY = monitor.getClientOffset().y - top;

			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

			moveField(dragIndex, hoverIndex);
			item.index = hoverIndex;
		},
	});

	const [{ isDragging }, drag] = useDrag({
		type: DRAG_TYPE,
		item: () => ({ id: field.value, index }),
		collect: (monitor) => ({ isDragging: monitor.isDragging() }),
	});

	drag(drop(ref));

	return (
		<div
			ref={ref}
			data-handler-id={handlerId}
			style={{
				display: "flex",
				alignItems: "center",
				gap: "0.5rem",
				padding: "0.1rem 0.75rem",
				marginBottom: "0.35rem",
				borderRadius: "8px",
				background: isDragging ? "rgba(245,124,0,0.12)" : "var(--background-color)",
				border: "1px solid var(--primary-color)",
				opacity: isDragging ? 0.45 : 1,
				transition: "background 0.12s ease, opacity 0.12s ease",
				userSelect: "none",
				cursor: "grab",
			}}
		>
			<span style={{ color: "var(--text-color)", opacity: 0.35, fontSize: "1rem", flexShrink: 0, lineHeight: 1 }}>⠿</span>
			<Checkbox
				id={field.value}
				name={field.label}
				label={field.label}
				value={field.value}
				checked={checked}
				onChange={(e) => onCheckChange(field, e.target.checked)}
			/>
		</div>
	);
}

function IdleWarehouseDialog({ open, onClose }) {
	const [selectedDate, setSelectedDate] = useState(null);
	const [tableData, setTableData] = useState([]);
	const [hasFetched, setHasFetched] = useState(false);
	const { getIdleWarehouses, error, isLoading } = useIdleWarehouses();

	const handleSubmit = async () => {
		if (!selectedDate) {
			toastMessage(false, "Please select a date");
			return;
		}
		try {
			const {
				data: { error, message, data },
			} = await getIdleWarehouses({ date: dayjs(selectedDate).format("YYYY-MM-DD") });
			if (error) toastMessage(false, message);
			else {
				setTableData(data || []);
				setHasFetched(true);
			}
		} catch (err) {
			toastMessage(false, err?.data?.message || "Something went wrong");
		}
	};

	const idleWarehouseHeader = ["Warehouse Name"];
	const rows = tableData.map((row) => [tCell(row.plantName || "-")]);
	const excelRows = tableData.map((row) => [row.plantName || "-"]);

	const handleDownloadExcel = () => {
		if (!excelRows.length) {
			toastMessage(false, "No data to download");
			return;
		}
		downloadExcel({
			fileName: "Idle_Warehouses",
			sheet: "Idle Warehouses",
			tablePayload: { header: idleWarehouseHeader, body: excelRows },
		});
	};

	const handleClose = () => {
		setSelectedDate(null);
		setTableData([]);
		setHasFetched(false);
		onClose();
	};

	return (
		<Dialog open={open} handleClose={handleClose} title="Idle Warehouses" size="xs" handleSubmit={handleDownloadExcel} SubmitText="Download Excel" isSubmitDisabled={!hasFetched || !excelRows.length}>
			<p style={{ margin: "0 0 1rem 0", color: "var(--text-color)", opacity: 0.7, fontSize: "13px" }}>Pick a date to spot warehouses with zero vehicle movement.</p>
			<div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
				<DatePicker
					name="idleDate"
					label="Select Date"
					value={selectedDate}
					maxDATE={dayjs()}
					onChange={(val) => {
						setSelectedDate(val);
						setHasFetched(false);
						setTableData([]);
					}}
				/>
				<div style={{ width: "200px" }}>
					<Button text={isLoading ? "Fetching..." : "Get Data"} onClick={handleSubmit} disabled={isLoading} style={{ ...buttonClasses.lynkitOrangeFill, height: "40px" }} />
				</div>
			</div>
			{isLoading ? <Loader /> : !error && hasFetched ? <GenericTableInfinite header={idleWarehouseHeader} rows={rows} isLoading={false} /> : null}
		</Dialog>
	);
}

function useReportFieldMapping(payload) {
	const [getFieldMapping, { data, isLoading }] = useGetReportFieldsMutation();
	useEffect(() => {
		if (payload?.type) getFieldMapping({ type: payload.type });
	}, [payload?.type]);
	const { data: fieldMapping } = data || { data: [] };
	return { fieldMapping, isLoading };
}

function useGetReport() {
	const [fetchReport, { data, isLoading: isReportLoading, reset }] = useGetReportMutation({ fixedCacheKey: "getReport" });
	const { data: reportData, totalCount } = data || { data: "" };
	async function getReport(args) {
		const {
			data: { error, message },
		} = await fetchReport(args);
		if (error) toastMessage(false, message);
	}
	return { getReport, reportData, isReportLoading, clearData: reset, totalCount };
}

function useGetReportPdf() {
	const [fetchReportPdf, { isLoading: isPDFLoading }] = useGetReportMutation({ fixedCacheKey: "getReportPDF" });
	return { fetchReportPdf, isPDFLoading };
}

export default Report;
